import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketplaceAPI, reviewsAPI } from '../services/api';
import ReviewForm from '../components/ReviewForm';
import '../styles/MyAnnonces.css';

const MyPurchases = () => {
  const { t } = useTranslation();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);
  const [reviewedOrders, setReviewedOrders] = useState({});
  const [showReviewModal, setShowReviewModal] = useState(null);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const statusLabels = {
    'pending_payment': t('status.pendingPayment'),
    'paid': t('status.paid'),
    'shipped': t('status.shipped'),
    'delivered': t('status.delivered'),
    'completed': t('status.completed'),
    'cancelled': t('status.cancelled'),
    'refunded': t('status.refunded')
  };

  const statusColors = {
    'pending_payment': '#ffc107',
    'paid': '#17a2b8',
    'shipped': '#6f42c1',
    'delivered': '#fd7e14',
    'completed': '#28a745',
    'cancelled': '#6c757d',
    'refunded': '#dc3545'
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const response = await marketplaceAPI.getPurchaseHistory();
      setPurchases(response.data);

      // Check which completed orders have reviews
      const completedOrders = response.data.filter(p => p.status === 'completed');
      const reviewStatus = {};

      await Promise.all(completedOrders.map(async (order) => {
        try {
          const reviewResponse = await reviewsAPI.canReview(order.id);
          // canReview = true means NOT reviewed yet, so reviewedOrders should be false
          reviewStatus[order.id] = !reviewResponse.data.canReview;
        } catch (e) {
          console.error('Error checking review status:', e);
          // On error, assume NOT reviewed (show button)
          reviewStatus[order.id] = false;
        }
      }));

      setReviewedOrders(reviewStatus);
    } catch (err) {
      setError(t('myPurchases.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSuccess = () => {
    setShowReviewModal(null);
    setReviewedOrders(prev => ({ ...prev, [showReviewModal.id]: true }));
  };

  const handleConfirmDelivery = async (orderId) => {
    if (!window.confirm(t('confirms.confirmDelivery'))) {
      return;
    }

    try {
      setConfirmingId(orderId);
      await marketplaceAPI.confirmDelivery(orderId);
      fetchPurchases();
    } catch (err) {
      setError(err.response?.data?.message || t('myPurchases.confirmError'));
    } finally {
      setConfirmingId(null);
    }
  };

  if (loading) {
    return <div className="loading-container"><p>{t('common.loading')}</p></div>;
  }

  return (
    <div className="my-annonces-container">
      <div className="my-annonces-header">
        <h2>{t('myPurchases.title')}</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      {purchases.filter(p => p.status !== 'pending_payment').length === 0 ? (
        <div className="no-annonces">
          <p>{t('myPurchases.noPurchases')}</p>
          <button className="btn-primary" onClick={() => navigate('/')}>
            {t('myPurchases.discoverItems')}
          </button>
        </div>
      ) : (
        <div className="annonces-list">
          {purchases.filter(p => p.status !== 'pending_payment').map(purchase => {
            const imageUrl = purchase.product_image
              ? `${API_URL.replace('/api', '')}/uploads/thumbnails/${purchase.product_image}`
              : 'https://via.placeholder.com/100x100?text=?';

            return (
              <div key={purchase.id} className="annonce-item purchase-item">
                <img src={imageUrl} alt={purchase.product_title} className="annonce-thumb" />

                <div className="annonce-info">
                  <h3>{purchase.product_title}</h3>
                  <p className="seller-name">{t('myPurchases.sellerLabel', { name: purchase.seller_name })}</p>

                  <div className="purchase-details">
                    <p>{t('myPurchases.itemLabel', { price: purchase.product_price?.toFixed(2) })}</p>
                    {purchase.shipping_price > 0 && (
                      <p>{t('myPurchases.shippingLabel', { price: purchase.shipping_price?.toFixed(2) })}</p>
                    )}
                    <p>{t('myPurchases.protectionLabel', { price: purchase.buyer_protection?.toFixed(2) })}</p>
                    <p className="total-price">
                      <strong>{t('myPurchases.totalLabel', { price: purchase.total_amount?.toFixed(2) })}</strong>
                    </p>
                  </div>

                  <span
                    className="annonce-status"
                    style={{ backgroundColor: statusColors[purchase.status] }}
                  >
                    {statusLabels[purchase.status]}
                  </span>

                  {purchase.tracking_number && (
                    <p className="tracking-number">
                      {t('myPurchases.trackingNumber', { number: purchase.tracking_number })}
                    </p>
                  )}
                  {purchase.tracking_url && (
                    <a
                      className="tracking-link"
                      href={purchase.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('movements.trackParcel', 'Suivre le colis')}
                    </a>
                  )}
                </div>

                <div className="annonce-actions">
                  {/* Bouton confirmer réception si payé ou expédié */}
                  {['paid', 'shipped', 'delivered'].includes(purchase.status) && (
                    <button
                      className="btn-confirm-delivery"
                      onClick={() => handleConfirmDelivery(purchase.id)}
                      disabled={confirmingId === purchase.id}
                    >
                      {confirmingId === purchase.id ? t('movements.confirming') : t('movements.confirmReceived')}
                    </button>
                  )}

                  {purchase.status === 'completed' && (
                    <>
                      <span className="completed-badge">{t('movements.transactionCompleted')}</span>
                      <button
                        className="btn-leave-review"
                        onClick={() => setShowReviewModal(purchase)}
                      >
                        {t('movements.leaveReview')}
                      </button>
                    </>
                  )}

                  <button
                    className="btn-contact"
                    onClick={() => navigate(`/messages/${purchase.seller_id}`)}
                  >
                    {t('myPurchases.contactSeller')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info protection */}
      <div className="info-box-bottom">
        <h4>{t('myPurchases.protectionTitle')}</h4>
        <p>
          {t('myPurchases.protectionInfo')}
        </p>
      </div>

      {/* Modal d'avis */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <ReviewForm
              orderId={showReviewModal.id}
              sellerName={showReviewModal.seller_name}
              onSuccess={handleReviewSuccess}
              onCancel={() => setShowReviewModal(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPurchases;
